/** @flow */

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import actionCreators from "@modules/service/actionCreator";

import { Button, Loader, RightPanel, View, ManagerSelfService } from "@components";
import withErrorHandler from "@components/error";
import { withClientContext, type PropsFromClientContext } from "@modules/context/components/";
import {
  withPermissions,
  PermissionEnum,
  type PropsFromPermissions,
} from "@modules/account/components";
import type { State } from "@store/reducers";
import type { EmployeeIdentity } from "@modules/service/reducer/employee-identity/types";
import type { DataSecurityGeneralSettings } from "@modules/service/reducer/system-access/types";
import type { ManagerSelfServiceSettings } from "@modules/service/reducer/manager-self-service/types";
import {
  SecuritySettings,
  SystemAccessIpAddresses,
  withSystemAccessModals,
  EmailDomains,
  EmailDomainAddModal,
  HelpPanelSecurity,
} from "@modules/service/components";
import type { PropsFromSystemAccessModals } from "@modules/service/components/with-system-access-modals";
import * as serviceSelectors from "@modules/service/selectors";
import type { EmailControlSettings } from "@modules/service/reducer/email-controls/types";
import { parseSchema } from "@utils/schema";
import { split } from "@transformers/schema";

type PropsFromState = $Exact<{
  employeeIdentityData: EmployeeIdentity,
  dataSecurityGeneralSettings: DataSecurityGeneralSettings,
  isLoading: boolean,
  emailControlSettings: ?EmailControlSettings,
  isAddEmailDomainModalVisible: boolean,
  managerSelfServiceSettings: ManagerSelfServiceSettings,
  hasActiveManualReferrals: boolean,

  // clientId: number | null,
}>;

type PropsFromDispatch = $Exact<{
  updatePlatformDataSecurityEmployeeIdentity: typeof actionCreators.UpdatePlatformDataSecurityEmployeeIdentity,
  getEmployeeIdentityDataEffective: typeof actionCreators.GetEmployeeIdentityData,
  getDataSecurityGeneralSettings: typeof actionCreators.GetDataSecurityGeneralSettings,
  showNewIpRestrictionModal: typeof actionCreators.ToggleAddIpRestrictionModal,
  getEmailControlsSettings: typeof actionCreators.GetEmailControlsSettings,
  updateEmailControlsSettings: typeof actionCreators.UpdateEmailControlsSettings,
  showAddEmailDomainModal: Function,
  dismissAddEmailDomainModal: Function,
  toggleConfirmEnableMfaModal: typeof actionCreators.ToggleConfirmEnableMfaModal,
  getManagerSelfServiceSettings: typeof actionCreators.GetManagerSelfServiceSettings,
  updateManagerSelfServiceSettings: typeof actionCreators.UpdateManagerSelfServiceSettings,
  getReferralList: typeof actionCreators.GetReferralList,
}>;

type Props = $Exact<{
  clientId: number | null,
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromPermissions,
  ...PropsFromClientContext,
  ...PropsFromLocalize,
  ...PropsFromSystemAccessModals,
}>;

export function SecuritySettingsPage(props: Props) {
  const clientId = props.clientId || props?.client?.id;
  const [schema, setSchema] = useState(null);
  const [isPanelOpened, setIsPanelOpened] = useState(false);

  useEffect(() => {
    props.getEmployeeIdentityDataEffective({
      clientId,
    });
    props.getDataSecurityGeneralSettings({
      clientId,
    });
    props.getEmailControlsSettings({
      clientId,
    });
    props.getManagerSelfServiceSettings({
      clientId,
    });
    props.getReferralList({
      clientId,
      isActive: true,
    });

    parseSchema("DataSecurityEmailDomainsServiceSectionDTO").then(schema => {
      setSchema(schema);
    });
  }, [clientId]);

  function showAddIpRestrictionModal() {
    props.showNewIpRestrictionModal(true);
  }

  function updateEmailSettings(emailControlsSettings: EmailControlSettings) {
    props.updateEmailControlsSettings({
      emailControlsSettings,
      clientId,
    });
  }

  function showAdd() {
    props.showAddEmailDomainModal();
  }

  function addEmailDomain(formData: Object) {
    const emailDomainRestrictionWhitelist = (props.emailControlSettings || {})
      .emailDomainRestrictionWhitelist;

    props.updateEmailControlsSettings({
      emailControlsSettings: {
        ...props.emailControlSettings,
        emailDomainRestrictionWhitelist: [...emailDomainRestrictionWhitelist, formData],
      },
      clientId,
    });
  }

  function emailContentSettingsSchema() {
    if (!schema) {
      return {};
    }
    return split(schema, [
      "markEmailsAsPrivate",
      "supressAllEmailContent",
      "allowHyperlinksInInstructions",
    ]);
  }

  function emailDomainSchema() {
    if (!schema) {
      return {};
    }
    return schema.properties.emailDomainRestrictionWhitelist.items;
  }

  function handleChangeMfa() {
    const { multiFactorAuthenticationEnabled } = props.dataSecurityGeneralSettings;

    // if current flag is enabled directly send request and update the flag to FALSE(disabled)
    if (multiFactorAuthenticationEnabled) {
      props.updateSettings({
        ...props.dataSecurityGeneralSettings,
        multiFactorAuthenticationEnabled: !multiFactorAuthenticationEnabled,
      });
    } else {
      // if current flag is disabled, show a confirmation modal to update the flag to TRUE(enabled)
      props.toggleConfirmEnableMfaModal(true);
    }
  }

  const toggleRightPanel = () => {
    setIsPanelOpened(prev => !prev);
  };

  function updateManagerSelfServiceSettings(data: Object) {
    props.updateManagerSelfServiceSettings({ clientId, data });
  }

  return (
    <>
      {props.isLoading && <Loader />}

      <div className="d-flex align-items-center">
        <View className="message-box" margin={[0, 0, "1rem", 0]}>
          {props.t("SECURITY_SETTINGS_INFORMATION")}
        </View>
      </div>
      <View gap="6">
        <View gap={6}>
          <View direction="row" gap="6">
            <View className="gs-container" flex={1}>
              <SecuritySettings
                clientId={clientId}
                identityData={props.employeeIdentityData}
                handleIdentitySubmit={props.updatePlatformDataSecurityEmployeeIdentity}
                emailControlSettings={props.emailControlSettings}
                emailSchema={emailContentSettingsSchema()}
                onUpdateEmailSettings={updateEmailSettings}
                mfaEnabled={props.dataSecurityGeneralSettings.multiFactorAuthenticationEnabled}
                onMfaChange={handleChangeMfa}
              />
            </View>
            <View className="gs-container" flex={1}>
              <ManagerSelfService
                settings={props.managerSelfServiceSettings}
                onUpdateSettings={updateManagerSelfServiceSettings}
                hasActiveManualReferrals={props.hasActiveManualReferrals}
              />
            </View>
          </View>
          <View direction="row" gap="6">
            <View className="gs-container" flex={1}>
              <SystemAccessIpAddresses
                list={props.dataSecurityGeneralSettings.ipRestrictionWhitelist || []}
                settings={props.dataSecurityGeneralSettings}
                onUpdateSettings={props.updateSettings}
                onAdd={showAddIpRestrictionModal}
              />
            </View>
            <View className="gs-container" flex={1}>
              {props.emailControlSettings && (
                <EmailDomains
                  list={props.emailControlSettings.emailDomainRestrictionWhitelist}
                  settings={props.emailControlSettings}
                  onUpdateEmailSettings={updateEmailSettings}
                  onAdd={showAdd}
                />
              )}
            </View>
          </View>
        </View>

        {/*// TODO - if not going to be used, remove this*/}
        {isPanelOpened && (
          <RightPanel title={props.t("SECURITY")} onClose={toggleRightPanel}>
            <HelpPanelSecurity />
          </RightPanel>
        )}

        {props.isAddEmailDomainModalVisible && (
          <EmailDomainAddModal
            onDismissHandler={props.dismissAddEmailDomainModal}
            onSubmit={addEmailDomain}
            schema={emailDomainSchema()}
          />
        )}
      </View>
    </>
  );
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => {
  return {
    employeeIdentityData: serviceSelectors.getEmployeeIdentityData(state),
    isLoading: serviceSelectors.isLoading(state),
    dataSecurityGeneralSettings: serviceSelectors.dataSecurityGeneralSettings(state),
    emailControlSettings: serviceSelectors.getEmailControlSettings(state),
    isAddEmailDomainModalVisible: serviceSelectors.getAddEmailDomainModalVisible(state),
    managerSelfServiceSettings: serviceSelectors.selectManagerSelfServiceSettings(state),
    hasActiveManualReferrals: serviceSelectors.hasActiveManualReferrals(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  updatePlatformDataSecurityEmployeeIdentity:
    actionCreators.UpdatePlatformDataSecurityEmployeeIdentity,
  getEmployeeIdentityDataEffective: actionCreators.GetEmployeeIdentityData,
  getDataSecurityGeneralSettings: actionCreators.GetDataSecurityGeneralSettings,
  showNewIpRestrictionModal: actionCreators.ToggleAddIpRestrictionModal,
  getEmailControlsSettings: actionCreators.GetEmailControlsSettings,
  updateEmailControlsSettings: actionCreators.UpdateEmailControlsSettings,
  showAddEmailDomainModal: () => actionCreators.ToggleAddEmailDomainModal(true),
  dismissAddEmailDomainModal: () => actionCreators.ToggleAddEmailDomainModal(false),
  toggleConfirmEnableMfaModal: actionCreators.ToggleConfirmEnableMfaModal,
  getManagerSelfServiceSettings: actionCreators.GetManagerSelfServiceSettings,
  updateManagerSelfServiceSettings: actionCreators.UpdateManagerSelfServiceSettings,
  getReferralList: actionCreators.GetReferralList,
};

export default compose(
  withErrorHandler(),
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
  withClientContext(),
  withSystemAccessModals(),
  withPermissions({
    permissionId: PermissionEnum.ACCESS_ADMIN_SERVICE,
  }),
)(SecuritySettingsPage);
